import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Briefcase, Search, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import api from '@/services/api'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import type { PjAgradaFormData } from '@/components/PjAgrada/pjAgradaTypes'
import { PJ_AGRADA_STATUS_LABELS } from '@/components/PjAgrada/pjAgradaStatus'
import { onlyDigits, maskCep, maskCnpj, maskCpf, maskPhone } from '@/components/PjAgrada/pjAgradaFormat'
import { getFriendlyError } from '@/utils/getFriendlyError'
import {
  applyCepToPjForm,
  applyCnpjToPjForm,
  lookupPjCep,
  lookupPjCnpjPublic,
} from '@/utils/pjAgradaLookups'

type Props = {
  open: boolean
  onClose: () => void
  formData: PjAgradaFormData
  setFormData: (data: PjAgradaFormData) => void
  onSave: (e: React.FormEvent) => void
  saving?: boolean
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h4
      className='text-[var(--font-color-label)] uppercase tracking-wider font-medium mb-3'
      style={{ fontSize: 'var(--font-size-label)' }}
    >
      {children}
    </h4>
  )
}

export function PjAgradaFormModal({ open, onClose, formData, setFormData, onSave, saving }: Props) {
  const [cepLoading, setCepLoading] = useState(false)
  const [cnpjLoading, setCnpjLoading] = useState(false)
  const [portalUsers, setPortalUsers] = useState<Array<{ id: string; name: string; email: string }>>([])

  useEffect(() => {
    if (!open) return
    void api
      .get('/users', { params: { role: 'usuario_agrada', limit: 200 } })
      .then((res) => {
        const rows = (res.data?.data ?? res.data ?? []) as Array<{
          id: string
          name: string
          email: string
        }>
        setPortalUsers(rows)
      })
      .catch(() => setPortalUsers([]))
  }, [open])

  useEffect(() => {
    if (open) document.body.style.overflow = 'hidden'
    else document.body.style.overflow = ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [open])

  const handleCepBlur = async () => {
    const digits = onlyDigits(formData.cep)
    if (digits.length !== 8) return
    setCepLoading(true)
    try {
      const data = await lookupPjCep(digits)
      setFormData(applyCepToPjForm(formData, data))
    } catch (err: unknown) {
      toast.error(getFriendlyError(err, 'CEP não encontrado'))
    } finally {
      setCepLoading(false)
    }
  }

  const handleCnpjLookup = async () => {
    const digits = onlyDigits(formData.cnpj)
    if (digits.length !== 14) {
      toast.error('CNPJ inválido — informe 14 dígitos')
      return
    }
    setCnpjLoading(true)
    try {
      const data = await lookupPjCnpjPublic(digits)
      setFormData(applyCnpjToPjForm(formData, data))
      toast.success('Razão social e endereço preenchidos a partir do CNPJ')
    } catch (err: unknown) {
      toast.error(getFriendlyError(err, 'CNPJ não encontrado'))
    } finally {
      setCnpjLoading(false)
    }
  }

  if (!open) return null

  return createPortal(
    <AnimatePresence>
      <div className='fixed inset-0 z-50 flex items-center justify-center p-4'>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className='absolute inset-0 bg-black/60 backdrop-blur-sm'
          onClick={onClose}
        />

        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 8 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: 8 }}
          transition={{ duration: 0.15, ease: 'easeOut' }}
          className='relative w-full max-w-3xl max-h-[90vh] z-10 bg-[var(--bg-card)] border border-[var(--glass-border)] rounded-xl shadow-2xl flex flex-col overflow-hidden'
        >
          <div className='flex items-center justify-between p-5 border-b border-[var(--glass-border)]'>
            <div className='flex items-center gap-3'>
              <div className='w-10 h-10 rounded-lg bg-[var(--brand-primary-muted)] flex items-center justify-center'>
                <Briefcase size={18} className='text-[var(--brand-primary)]' />
              </div>
              <div>
                <h2 className='font-semibold text-[var(--font-color-h2)]' style={{ fontSize: 'var(--font-size-h3)' }}>
                  {formData.id ? 'Editar funcionário PJ' : 'Novo funcionário PJ'}
                </h2>
                <p className='text-[var(--font-color-muted)]' style={{ fontSize: 'var(--font-size-xs)' }}>
                  Cadastro dos colaboradores PJ (funcionários) da Agrada — dados pessoais, contrato e pagamento
                </p>
              </div>
            </div>
            <button
              type='button'
              onClick={onClose}
              className='p-1.5 rounded-md text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--glass-bg)] transition-colors'
            >
              <X size={16} />
            </button>
          </div>

          <form onSubmit={onSave} className='min-h-0 flex-1 overflow-y-auto p-5 space-y-6'>
            <div>
              <SectionTitle>Identificação</SectionTitle>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
                <Input
                  label='Nome completo *'
                  required
                  value={formData.nomeCompleto}
                  onChange={(e) => setFormData({ ...formData, nomeCompleto: e.target.value })}
                />
                <Input
                  label='Razão social'
                  value={formData.razaoSocial}
                  onChange={(e) => setFormData({ ...formData, razaoSocial: e.target.value })}
                  placeholder='Se PJ com CNPJ'
                />
                <Input
                  label='CPF'
                  value={formData.cpf}
                  onChange={(e) => setFormData({ ...formData, cpf: maskCpf(e.target.value) })}
                  placeholder='000.000.000-00'
                  inputMode='numeric'
                  maxLength={14}
                />
                <div className='flex gap-2'>
                  <div className='flex-1'>
                    <Input
                      label='CNPJ'
                      value={formData.cnpj}
                      onChange={(e) => setFormData({ ...formData, cnpj: maskCnpj(e.target.value) })}
                      placeholder='00.000.000/0000-00'
                      inputMode='numeric'
                      maxLength={18}
                    />
                  </div>
                  <div className='flex items-end'>
                    <Button
                      type='button'
                      variant='secondary'
                      size='sm'
                      disabled={cnpjLoading}
                      onClick={() => void handleCnpjLookup()}
                      icon={cnpjLoading ? <Loader2 size={14} className='animate-spin' /> : <Search size={14} />}
                      title='Buscar na Receita'
                    />
                  </div>
                </div>
                <Input
                  label='Telefone *'
                  required
                  value={formData.telefone}
                  onChange={(e) => setFormData({ ...formData, telefone: maskPhone(e.target.value) })}
                  placeholder='(00) 00000-0000'
                  inputMode='tel'
                />
                <Input
                  label='E-mail'
                  type='email'
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
                <Input
                  label='Cargo / setor'
                  value={formData.areaAtuacao}
                  onChange={(e) => setFormData({ ...formData, areaAtuacao: e.target.value })}
                  placeholder='Ex: Closer, Designer, Desenvolvedor'
                />
                <Input
                  label='Data de admissão'
                  type='date'
                  value={formData.dataInicioContrato}
                  onChange={(e) => setFormData({ ...formData, dataInicioContrato: e.target.value })}
                />
              </div>
            </div>

            <div>
              <SectionTitle>Endereço</SectionTitle>
              <div className='grid grid-cols-1 md:grid-cols-3 gap-3'>
                <Input
                  label='CEP'
                  value={formData.cep}
                  onChange={(e) => setFormData({ ...formData, cep: maskCep(e.target.value) })}
                  onBlur={() => void handleCepBlur()}
                  placeholder='00000-000'
                  inputMode='numeric'
                  maxLength={9}
                  hint={cepLoading ? 'Buscando endereço…' : undefined}
                />
                <div className='md:col-span-2'>
                  <Input
                    label='Logradouro'
                    value={formData.logradouro}
                    onChange={(e) => setFormData({ ...formData, logradouro: e.target.value })}
                  />
                </div>
                <Input
                  label='Número'
                  value={formData.numero}
                  onChange={(e) => setFormData({ ...formData, numero: e.target.value })}
                />
                <Input
                  label='Complemento'
                  value={formData.complemento}
                  onChange={(e) => setFormData({ ...formData, complemento: e.target.value })}
                />
                <Input
                  label='Bairro'
                  value={formData.bairro}
                  onChange={(e) => setFormData({ ...formData, bairro: e.target.value })}
                />
                <Input
                  label='Cidade'
                  value={formData.cidade}
                  onChange={(e) => setFormData({ ...formData, cidade: e.target.value })}
                />
                <Input
                  label='UF'
                  value={formData.uf}
                  onChange={(e) => setFormData({ ...formData, uf: e.target.value.toUpperCase().slice(0, 2) })}
                  placeholder='SP'
                />
              </div>
            </div>

            <div>
              <SectionTitle>Dados bancários</SectionTitle>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
                <Input
                  label='Banco'
                  value={formData.bancoNome}
                  onChange={(e) => setFormData({ ...formData, bancoNome: e.target.value })}
                />
                <div>
                  <label className='block text-[var(--font-color-label)] mb-1.5' style={{ fontSize: 'var(--font-size-label)' }}>
                    Tipo de conta
                  </label>
                  <select
                    value={formData.bancoTipoConta}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        bancoTipoConta: e.target.value as PjAgradaFormData['bancoTipoConta'],
                      })
                    }
                    className='w-full rounded-lg border border-[var(--glass-border)] bg-[var(--glass-bg)] px-3 py-2 text-sm'
                  >
                    <option value=''>Selecione</option>
                    <option value='corrente'>Corrente</option>
                    <option value='poupanca'>Poupança</option>
                  </select>
                </div>
                <Input
                  label='Agência'
                  value={formData.bancoAgencia}
                  onChange={(e) => setFormData({ ...formData, bancoAgencia: e.target.value })}
                />
                <Input
                  label='Conta'
                  value={formData.bancoConta}
                  onChange={(e) => setFormData({ ...formData, bancoConta: e.target.value })}
                />
                <div className='md:col-span-2'>
                  <Input
                    label='Chave PIX'
                    value={formData.pixChave}
                    onChange={(e) => setFormData({ ...formData, pixChave: e.target.value })}
                    placeholder='CPF, e-mail, telefone ou aleatória'
                  />
                </div>
              </div>
            </div>

            <div>
              <SectionTitle>Status e observações</SectionTitle>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
                <div>
                  <label className='block text-[var(--font-color-label)] mb-1.5' style={{ fontSize: 'var(--font-size-label)' }}>
                    Usuário do portal (PJ)
                  </label>
                  <select
                    value={formData.portalUserId}
                    onChange={(e) => setFormData({ ...formData, portalUserId: e.target.value })}
                    className='w-full rounded-lg border border-[var(--glass-border)] bg-[var(--glass-bg)] px-3 py-2 text-sm'
                  >
                    <option value=''>Sem vínculo</option>
                    {portalUsers.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name} · {u.email}
                      </option>
                    ))}
                  </select>
                  <p className='mt-1 text-[11px] text-[var(--font-color-muted)]'>
                    Vincule manualmente o login do portal para envio de NF mensal.
                  </p>
                </div>
                <div>
                  <label className='block text-[var(--font-color-label)] mb-1.5' style={{ fontSize: 'var(--font-size-label)' }}>
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) =>
                      setFormData({ ...formData, status: e.target.value as PjAgradaFormData['status'] })
                    }
                    className='w-full rounded-lg border border-[var(--glass-border)] bg-[var(--glass-bg)] px-3 py-2 text-sm'
                  >
                    {(Object.keys(PJ_AGRADA_STATUS_LABELS) as PjAgradaFormData['status'][]).map((status) => (
                      <option key={status} value={status}>
                        {PJ_AGRADA_STATUS_LABELS[status]}
                      </option>
                    ))}
                  </select>
                </div>
                <div className='md:col-span-2'>
                  <label className='block text-[var(--font-color-label)] mb-1.5' style={{ fontSize: 'var(--font-size-label)' }}>
                    Observações
                  </label>
                  <textarea
                    value={formData.observacoes}
                    onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                    rows={3}
                    className='w-full rounded-lg border border-[var(--glass-border)] bg-[var(--glass-bg)] px-3 py-2 text-sm'
                    placeholder='Notas internas do financeiro'
                  />
                </div>
              </div>
            </div>

            <div className='flex justify-end gap-2 pt-2 border-t border-[var(--glass-border)]'>
              <Button type='button' variant='secondary' onClick={onClose}>
                Cancelar
              </Button>
              <Button type='submit' variant='primary' disabled={saving}>
                {saving ? 'Salvando…' : formData.id ? 'Salvar alterações' : 'Cadastrar funcionário'}
              </Button>
            </div>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>,
    document.body,
  )
}
